import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/AuthProvider';
import LoginForm from '../components/LoginForm';
import SignupForm from '../components/SignupForm';
import MobileChatRoom from './MobileChatRoom';
import MobileLandingPage from './MobileLandingPage';
import MobileRoomSelectionModal from './MobileRoomSelectionModal';
import { useNarrativeRooms } from '../shared/hooks/useNarrativeRooms';
import api from '../utils/api';

/**
 * Mobile App Root Component
 * Handles the mobile-specific app flow and UI
 */
const MobileRoot = () => {
  const { user, signOut, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [roomSelectionData, setRoomSelectionData] = useState(null);
  const [showRoomSelection, setShowRoomSelection] = useState(false);
  const [processingInvite, setProcessingInvite] = useState(false);
  const [inviteLoadingMessage, setInviteLoadingMessage] = useState('Loading story room...');

  // Set force mobile flag when MobileRoot mounts
  useEffect(() => {
    sessionStorage.setItem('forceMobile', 'true');
    
    return () => {
      // Clean up on unmount, but only if we're not still on mobile
      if (window.innerWidth > 640) {
        sessionStorage.removeItem('forceMobile');
      }
    };
  }, []);

  // Use shared narrative/room logic
  const {
    narratives,
    loadingNarratives,
    currentRoom,
    setCurrentRoom,
    loadNarratives,
    createStableRoom,
    createNewRoomForNarrative,
    handleJoinAsGuest,
    discoverUserRooms,
    joinSpecificRoom,
    deleteRoom
  } = useNarrativeRooms();

  // Stable onLeave callback to prevent ChatRoom re-renders
  const handleLeaveRoom = useCallback(() => {
    setCurrentRoom(null);
  }, []);

  // Memoized current room to prevent unnecessary re-renders
  const stableCurrentRoom = useMemo(() => {
    if (!currentRoom) return null;
    
    // If room already has stable structure, return as-is
    if (currentRoom.narrative && typeof currentRoom.id !== 'undefined') {
      return currentRoom;
    }
    
    return currentRoom;
  }, [
    currentRoom?.id, 
    currentRoom?.narrative?.id, 
    currentRoom?.isGuest,
    currentRoom?.guest_user_id
  ]);

  // Function to handle room invite joins via /join/{room_id} URLs
  const handleRoomInvite = async (roomId, prefillName) => {
    try {
      setProcessingInvite(true);
      setInviteLoadingMessage('Loading story room...');

      // First, verify the room exists and get its details
      const roomResponse = await api.get(`/scenes/validate/${roomId}`);

      if (roomResponse.narrative?.title) {
        setInviteLoadingMessage(`Loading ${roomResponse.narrative.title}...`);
      }

      if (roomResponse && roomResponse.scene && roomResponse.scene.room_id) {
        const narrativeTitle = roomResponse.narrative?.title || 'Unknown Story';
        const narrativeData = roomResponse.narrative || { id: 'unknown', title: narrativeTitle };

        if (user) {
          // Authenticated path: join as real user — add to participants, use JWT for WS
          setInviteLoadingMessage(`Joining ${narrativeTitle}...`);
          await api.joinRoom({ room_id: roomId });

          const roomData = {
            id: roomResponse.scene.room_id,
            name: roomResponse.room_data?.name || narrativeTitle,
            room_code: roomResponse.room_data?.room_code || null,
            isGuest: false,
          };

          const stableRoom = createStableRoom(roomData, narrativeData, false, { isInviteJoin: true, roomResponse });
          window.history.replaceState({}, document.title, '/');
          setCurrentRoom(stableRoom);
          setProcessingInvite(false);
          return true;

        } else {
          // Guest path: create/get guest identity, use guest token for WS
          setInviteLoadingMessage('Creating guest user...');

          const existingGuest = sessionStorage.getItem('guest_user') ? JSON.parse(sessionStorage.getItem('guest_user')) : null;
          const guestResponse = await api.createOrGetGuestUser({
            room_id: roomId,
            existing_guest_id: existingGuest?.id || null,
            display_name: existingGuest ? null : (prefillName || null),
          });

          if (!guestResponse.success) {
            throw new Error('Failed to create/get guest user');
          }

          const guestUser = guestResponse.guest_user;
          const context = guestResponse.context;

          sessionStorage.setItem('guest_user', JSON.stringify(guestUser));
          setInviteLoadingMessage(`Joining ${narrativeTitle}...`);

          sessionStorage.setItem('guestSession', JSON.stringify({
            user: guestUser,
            session: { access_token: `guest_${guestUser.id}`, user: guestUser }
          }));

          const roomData = {
            id: roomResponse.scene.room_id,
            name: roomResponse.room_data?.name || context.scene_title || roomResponse.scene.state_data?.scene_title || `Room ${roomResponse.scene.room_id.substr(0, 8)}`,
            room_code: roomResponse.room_data?.room_code || null,
            guest_user_id: guestUser.id,
            isGuest: true,
          };

          const guestNarrativeData = roomResponse.narrative || context.narrative || context.inferred_narrative || { id: 'unknown', title: 'Unknown Story' };

          const stableRoom = createStableRoom(roomData, guestNarrativeData, true, { isInviteJoin: true, roomResponse, guest_user_id: guestUser.id });
          window.history.replaceState({}, document.title, '/');
          setCurrentRoom(stableRoom);
          setProcessingInvite(false);
          return true;
        }

      } else {
        throw new Error('Room not found or invalid response format');
      }
    } catch (error) {
      console.error('Error in handleRoomInvite:', error);
      setProcessingInvite(false);
      setInviteLoadingMessage('Loading story room...');
      window.history.replaceState({}, document.title, '/');
      throw error;
    }
  };

  useEffect(() => {
    loadNarratives(); // Load narratives normally with fast endpoint
    
    // Handle room invite URLs and email confirmation
    const handleURLParameters = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pathname = window.location.pathname;
      
      // Handle room invite URLs: /join/{room_id}
      const roomInviteMatch = pathname.match(/\/join\/([a-f0-9-]{36})/);
      if (roomInviteMatch) {
        const inviteRoomId = roomInviteMatch[1];
        const prefillName = urlParams.get('name') || null;

        try {
          await handleRoomInvite(inviteRoomId, prefillName);
          return;
        } catch (error) {
          console.error('Error handling room invite join:', error);
        }
      }
      
      // Handle email confirmation from URL parameters
      const token = urlParams.get('token');
      const type = urlParams.get('type');
      const confirmed = urlParams.get('confirmed');
      
      if (token && type === 'signup') {
        setEmailConfirmed(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (confirmed === 'true') {
        setEmailConfirmed(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      if (error) {
        console.error('Email confirmation error:', error, errorDescription);
      }
    };
    
    handleURLParameters();
  }, []);

  const handleNarrativeSelect = async (narrative) => {
    
    if (user) {
      // Authenticated user - use shared room discovery logic
      try {
        const userId = user.id; // Get user ID from auth context
        const result = await discoverUserRooms(narrative, userId);
        
        if (result.hasMultipleRooms) {
          setRoomSelectionData({
            narrative: narrative,
            rooms: result.rooms
          });
          setShowRoomSelection(true);
        }
        // If single room, discoverUserRooms already sets currentRoom
      } catch (error) {
        console.error('Room discovery error:', error);
      }
    } else {
      // Guest user - use shared guest logic
      try {
        const result = await handleJoinAsGuest(narrative);
        
        if (result.hasMultipleRooms) {
          setRoomSelectionData({
            narrative: narrative,
            rooms: result.rooms,
            isGuest: true,
            guestUser: result.guestUser
          });
          setShowRoomSelection(true);
        }
        // If single room, handleJoinAsGuest already sets currentRoom
      } catch (error) {
        console.error('Guest room error:', error);
      }
    }
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    if (selectedNarrative) {
      handleNarrativeSelect(selectedNarrative);
      setSelectedNarrative(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignIn = () => {
    setAuthMode('login');
    setShowAuth(true);
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show chat room if user has joined a story
  if (stableCurrentRoom) {
    const handleGuestSignIn = () => {
      setAuthMode('signup');
      setShowAuth(true);
    };
    
    return (
      <>
        <MobileChatRoom 
          room={stableCurrentRoom} 
          user={user} 
          onLeave={handleLeaveRoom} 
          onSignIn={handleGuestSignIn} 
        />
        
        {/* Auth Modal */}
        {showAuth && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-md">
              <button
                onClick={() => setShowAuth(false)}
                className="absolute -top-4 -right-4 bg-gray-700 hover:bg-gray-600 text-white w-8 h-8 rounded-full flex items-center justify-center z-10"
              >
                ×
              </button>
              {authMode === 'login' ? (
                <LoginForm
                  onClose={handleAuthSuccess}
                  onSwitchToSignup={() => setAuthMode('signup')}
                />
              ) : (
                <SignupForm
                  onClose={handleAuthSuccess}
                  onSwitchToLogin={() => setAuthMode('login')}
                />
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Show loading spinner when processing invite instead of landing page */}
      {processingInvite ? (
        <div className="h-screen bg-gray-900 flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6"></div>
            <h2 className="text-2xl font-bold text-white mb-2">🎭 PlayScreen</h2>
            <p className="text-gray-300 text-lg">{inviteLoadingMessage}</p>
          </div>
        </div>
      ) : (
        <>
          <MobileLandingPage
            narratives={narratives}
            user={user}
            onNarrativeSelect={handleNarrativeSelect}
            onSignIn={handleSignIn}
            onSignOut={handleSignOut}
            loadingNarratives={loadingNarratives}
          />

          {/* Auth Modal */}
          {showAuth && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="relative w-full max-w-md">
                <button
                  onClick={() => setShowAuth(false)}
                  className="absolute -top-4 -right-4 bg-gray-700 hover:bg-gray-600 text-white w-8 h-8 rounded-full flex items-center justify-center z-10"
                >
                  ×
                </button>
                {authMode === 'login' ? (
                  <LoginForm
                    onClose={handleAuthSuccess}
                    onSwitchToSignup={() => setAuthMode('signup')}
                  />
                ) : (
                  <SignupForm
                    onClose={handleAuthSuccess}
                    onSwitchToLogin={() => setAuthMode('login')}
                  />
                )}
              </div>
            </div>
          )}

          {/* Email Confirmation Message */}
          {emailConfirmed && (
            <div className="fixed bottom-4 left-4 right-4 bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-center z-50">
              <p>✅ Email confirmed! You can now sign in.</p>
              <button
                onClick={() => {
                  setEmailConfirmed(false);
                  setShowAuth(true);
                  setAuthMode('login');
                }}
                className="mt-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                >
                Sign In Now
              </button>
            </div>
          )}

          {/* Room Selection Modal */}
          {showRoomSelection && roomSelectionData && (
            <MobileRoomSelectionModal
              isOpen={showRoomSelection}
              onClose={() => {
                setShowRoomSelection(false);
                setRoomSelectionData(null);
              }}
              rooms={roomSelectionData.rooms}
              narrativeName={roomSelectionData.narrative?.title || 'Adventure'}
              onJoinRoom={async (room) => {
                try {
                  await joinSpecificRoom(room, roomSelectionData.narrative);
                  setShowRoomSelection(false);
                  setRoomSelectionData(null);
                } catch (error) {
                  console.error('Error joining room:', error);
                  alert('Failed to join room. Please try again.');
                }
              }}
              onDeleteRoom={async (roomId) => {
                try {
                  await deleteRoom(roomId);
                  // Refresh the room list
                  const updatedRooms = roomSelectionData.rooms.filter(r => r.id !== roomId);
                  if (updatedRooms.length === 0) {
                    // No rooms left - close modal and return to narrative list
                    setShowRoomSelection(false);
                    setRoomSelectionData(null);
                    // Don't auto-create room on mobile - let user choose
                  } else if (updatedRooms.length === 1) {
                    // Only one room left - on mobile, still show the option instead of auto-joining
                    setRoomSelectionData(prev => ({
                      ...prev,
                      rooms: updatedRooms
                    }));
                  } else {
                    // Still multiple rooms - update the list
                    setRoomSelectionData(prev => ({
                      ...prev,
                      rooms: updatedRooms
                    }));
                  }
                } catch (error) {
                  console.error('Error deleting room:', error);
                  alert('Failed to delete room. Please try again.');
                }
              }}
              onCreateNew={async () => {
                try {
                  setShowRoomSelection(false);
                  setRoomSelectionData(null);
                  await createNewRoomForNarrative(roomSelectionData.narrative);
                } catch (error) {
                  console.error('Error creating new room:', error);
                  alert('Failed to create new room. Please try again.');
                }
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default MobileRoot;